import React, { useState, useMemo } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { CheckCircle2, AlertCircle } from "lucide-react";
import axios from "axios";
import { Tx } from "../context/AutoTranslateContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";

/**
 * Drop-in "Pay with PayPal" block that mirrors your existing Stripe checkout flow.
 * Props:
 *   productType    — one of: illness_pdf | diploma_course | exam_booking |
 *                    diploma_certificate | hijama_atlas_book | shop_item
 *   payload        — extra fields sent to /api/paypal/orders (illness_id,
 *                    email, full_name, phone, cert_token, shop_product_id)
 *   disabled       — hide buttons (e.g. missing required form fields)
 *   onPaid(tx)     — called after a successful capture. Receives {order_id, capture_id}.
 *                    A parent typically navigates to /payment-success?session_id=order_id.
 *   currency       — 3-letter ISO code (default "GBP")
 */
const PayPalCheckout = ({ productType, payload = {}, disabled = false, onPaid, currency = "GBP" }) => {
  const [status, setStatus] = useState("idle"); // idle | processing | paid | error
  const [error, setError] = useState("");

  const options = useMemo(() => ({
    "client-id": CLIENT_ID || "sb", // "sb" = sandbox demo — safe placeholder when unset
    currency,
    intent: "capture",
    components: "buttons",
  }), [currency]);

  if (!CLIENT_ID) {
    return (
      <div data-testid="paypal-not-configured" className="text-xs text-mute italic border border-forest/10 rounded-2xl p-3 text-center">
        <Tx>PayPal is not configured yet. Ask the site owner to add their PayPal Client ID.</Tx>
      </div>
    );
  }

  const createOrder = async () => {
    setError(""); setStatus("processing");
    try {
      const { data } = await axios.post(`${API}/paypal/orders`,
        { product_type: productType, ...payload });
      return data.order_id;
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Could not start PayPal checkout";
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
      setStatus("error");
      throw e;
    }
  };

  const onApprove = async (data) => {
    try {
      const r = await axios.post(`${API}/paypal/orders/${data.orderID}/capture`, {});
      if (r.data?.status !== "COMPLETED") throw new Error("Capture failed");
      setStatus("paid");
      onPaid?.({ order_id: data.orderID, capture_id: r.data.capture_id });
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Payment could not be completed";
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
      setStatus("error");
    }
  };

  return (
    <div data-testid={`paypal-block-${productType}`} className={`space-y-2 ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
      <PayPalScriptProvider options={options}>
        <PayPalButtons
          style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal", height: 44 }}
          disabled={disabled}
          forceReRender={[productType, JSON.stringify(payload), currency]}
          createOrder={createOrder}
          onApprove={onApprove}
          onCancel={() => { setStatus("idle"); setError(""); }}
          onError={(e) => { setError(e?.message || "PayPal error"); setStatus("error"); }}
        />
      </PayPalScriptProvider>
      {status === "paid" && (
        <div data-testid="paypal-success" className="flex items-center gap-2 text-sm text-forest bg-forest/10 rounded-2xl p-3">
          <CheckCircle2 className="w-4 h-4" /> <Tx>Payment complete. Redirecting…</Tx>
        </div>
      )}
      {error && (
        <div data-testid="paypal-error" className="flex items-start gap-2 text-sm text-clay bg-clay/10 rounded-2xl p-3">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default PayPalCheckout;
